import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { catalogAdminApi } from '../../api/catalogAdmin';
import { queryKeys } from '../queryClient';
import type { CatalogAreaInput, CatalogItemInput, CatalogPackageInput } from '../../types/domain';

/**
 * Catalog admin (manager+). Areas and items have a real admin list (incl. inactive rows
 * and the id PUT/DELETE need) — their mutations invalidate both that and the public
 * catalog query. Packages have no admin list; their mutations invalidate only the public
 * catalog (useCatalog()) that the admin table reads from.
 */
export function useServiceAreasAdmin() {
  return useQuery({ queryKey: queryKeys.catalogAdminAreas, queryFn: () => catalogAdminApi.listAreas() });
}

export function useCreateServiceArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CatalogAreaInput) => catalogAdminApi.createArea(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.catalog });
      qc.invalidateQueries({ queryKey: queryKeys.catalogAdminAreas });
    },
  });
}

export function useUpdateServiceArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CatalogAreaInput }) => catalogAdminApi.updateArea(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.catalog });
      qc.invalidateQueries({ queryKey: queryKeys.catalogAdminAreas });
    },
  });
}

export function useDeleteServiceArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogAdminApi.deleteArea(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.catalog });
      qc.invalidateQueries({ queryKey: queryKeys.catalogAdminAreas });
    },
  });
}

export function useCreatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CatalogPackageInput) => catalogAdminApi.createPackage(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.catalog }),
  });
}

export function useUpdatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CatalogPackageInput }) => catalogAdminApi.updatePackage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.catalog }),
  });
}

export function useDeletePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogAdminApi.deletePackage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.catalog }),
  });
}

export function useCleaningItemsAdmin() {
  return useQuery({ queryKey: queryKeys.catalogAdminItems, queryFn: () => catalogAdminApi.listItems() });
}

export function useCreateCleaningItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CatalogItemInput) => catalogAdminApi.createItem(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.catalog });
      qc.invalidateQueries({ queryKey: queryKeys.catalogAdminItems });
    },
  });
}

export function useUpdateCleaningItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CatalogItemInput }) => catalogAdminApi.updateItem(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.catalog });
      qc.invalidateQueries({ queryKey: queryKeys.catalogAdminItems });
    },
  });
}

export function useDeleteCleaningItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogAdminApi.deleteItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.catalog });
      qc.invalidateQueries({ queryKey: queryKeys.catalogAdminItems });
    },
  });
}
